// Browser self-test: drives the real UI with synthetic pointer and control
// events and checks what the meter shows. Open index.html?selftest=1
// (with wobble=off&tolerances=off for exact numbers). Result goes to #status.

import type { Point } from "./geometry";

export interface TestHooks {
  svg: SVGSVGElement;
  holeScene(id: string): Point | undefined;
  terminalScene(instrument: string, terminal: string): Point | undefined;
  frames(n: number): Promise<void>;
  view(): unknown;
  activity(): unknown;
  apply(action: unknown): { ok: boolean; error?: string };
}

function pointerEvent(type: string, target: Element, p: Point): void {
  target.dispatchEvent(new PointerEvent(type, { clientX: p.x, clientY: p.y, pointerId: 1, pointerType: "mouse", isPrimary: true, bubbles: true, cancelable: true, button: 0 }));
}

// Turn a supply knob by dragging it vertically to the value wanted.
async function knob(h: TestHooks, name: "volt" | "lim", value: number): Promise<void> {
  const g = h.svg.querySelector<SVGElement>(`[data-knob="${name}"]`);
  if (!g) throw new Error(`no ${name} knob`);
  const now = Number(g.getAttribute("aria-valuenow"));
  const perMm = name === "volt" ? 0.5 : 0.05;
  const scale = h.svg.getScreenCTM()!.a;
  const c = g.getBoundingClientRect();
  const from = { x: c.left + c.width / 2, y: c.top + c.height / 2 };
  const to = { x: from.x, y: from.y - ((value - now) / perMm) * scale };
  pointerEvent("pointerdown", g, from);
  pointerEvent("pointermove", h.svg, to);
  pointerEvent("pointerup", h.svg, to);
  await h.frames(2);
  const got = Number(g.getAttribute("aria-valuenow"));
  if (Math.abs(got - value) > 1e-6) throw new Error(`${name} knob: wanted ${value}, got ${got}`);
}

// Press the supply's OUTPUT button.
async function output(h: TestHooks): Promise<void> {
  (h.svg.querySelector("#psu-on") as SVGElement).dispatchEvent(new MouseEvent("click", { bubbles: true }));
  await h.frames(2);
}

export async function runSelfTest(h: TestHooks): Promise<void> {
  const status = document.getElementById("status")!;
  if (new URLSearchParams(location.search).get("selftest") === "activity") return runActivitySelfTest(h);
  const steps: string[] = [];
  const fail = (msg: string) => { throw new Error(msg); };
  const toClient = (p: Point): Point => {
    const pt = h.svg.createSVGPoint();
    pt.x = p.x; pt.y = p.y;
    const q = pt.matrixTransform(h.svg.getScreenCTM()!);
    return { x: q.x, y: q.y };
  };
  const pointer = (type: string, target: Element, p: Point) => {
    target.dispatchEvent(new PointerEvent(type, { clientX: p.x, clientY: p.y, pointerId: 1, pointerType: "mouse", isPrimary: true, bubbles: true, cancelable: true, button: 0 }));
  };
  const tap = async (target: Element, p: Point) => { pointer("pointerdown", target, p); pointer("pointerup", target, p); await h.frames(2); };
  const click = async (id: string) => { (document.getElementById(id) as HTMLElement).click(); await h.frames(2); };
  const hole = (id: string) => toClient(h.holeScene(id) ?? fail(`no hole ${id}`));
  const terminal = (i: string, t: string) => toClient(h.terminalScene(i, t) ?? fail(`no terminal ${i} ${t}`));
  const check = (name: string, ok: boolean, detail = "") => { if (!ok) fail(`${name} ${detail}`.trim()); steps.push(name); };
  const text = (id: string) => document.getElementById(id)?.textContent ?? "";
  const setRadio = async (name: string, value: string) => {
    const r = document.querySelector<HTMLInputElement>(`input[name=${name}][value="${value}"]`)!;
    r.checked = true; r.dispatchEvent(new Event("change")); await h.frames(2);
  };
  const dragProbe = async (lead: "red" | "black", to: Point) => {
    const tip = h.svg.querySelector<SVGElement>(`.probe-tip[data-probe="${lead}"]`) ?? fail(`no ${lead} probe tip`);
    const r = tip.getBoundingClientRect();
    const from = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    pointer("pointerdown", tip, from);
    pointer("pointermove", h.svg, { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 });
    pointer("pointermove", h.svg, to);
    pointer("pointerup", h.svg, to);
    await h.frames(2);
  };

  try {
    await h.frames(2);
    // Resistor a5..a9, LED e9..e10 (anode shares the strip with R1's second leg).
    await click("place-resistor");
    await tap(h.svg, hole("a5"));
    check("place resistor", !!h.svg.querySelector('[data-part="R1"]'));
    await click("place-led");
    await tap(h.svg, hole("e9"));
    check("place led", !!h.svg.querySelector('[data-part="D1"]'));
    await click("tool-cancel");
    // Wires: supply + to b5, supply − to b10.
    await click("tool-wire");
    await tap(h.svg, terminal("psu", "pos"));
    await tap(h.svg, hole("b5"));
    await tap(h.svg, terminal("psu", "neg"));
    await tap(h.svg, hole("b10"));
    await click("tool-cancel");
    check("wires", h.svg.querySelectorAll(".lead").length === 2, `${h.svg.querySelectorAll(".lead").length} leads`);
    // Supply 5 V, 1 A, on.
    await knob(h, "volt", 7);
    await knob(h, "volt", 5);
    await knob(h, "lim", 1);
    check("standby display", text("psu-v") === "5.00 V", `got "${text("psu-v")}"`);
    await output(h);
    check("output lamp", document.getElementById("psu-on")!.classList.contains("on"));
    // Meter: V⎓ on the VΩ jack, probes across the LED (c9, c10).
    await setRadio("dmm-mode", "vdc");
    await setRadio("dmm-jack", "V");
    await dragProbe("red", hole("c9"));
    await dragProbe("black", hole("c10"));
    await h.frames(150);
    check("led voltage", text("dmm-text") === "1.879 V", `got "${text("dmm-text")}"`);
    check("supply cv", document.getElementById("psu-cv")!.classList.contains("on"));
    check("supply display", text("psu-v") === "5.00 V", `got "${text("psu-v")}"`);
    const glow = h.svg.querySelector("#glow-D1")!;
    check("led lit", Number(glow.getAttribute("opacity")) > 0.3, `opacity ${glow.getAttribute("opacity")}`);
    // Resistor key appears on focus.
    (h.svg.querySelector('[data-part="R1"]') as SVGElement).focus();
    h.svg.querySelector('[data-part="R1"]')!.dispatchEvent(new FocusEvent("focusin", { bubbles: true }));
    await h.frames(2);
    const key = document.getElementById("key")!;
    check("band key", !key.hidden && key.textContent!.includes("red, red, brown, gold = 220 Ω ±5%"), `key "${key.textContent}"`);
    // Switch to the mA jack and measure current across the supply: fuse blows.
    await setRadio("dmm-mode", "adc");
    await setRadio("dmm-jack", "mA");
    const st = (h.view() as { state: { meter: { redJack: string }; supply: { currentLimit: number } } }).state;
    if (st.meter.redJack !== "mA") {
      const r = h.apply({ type: "setMeter", redJack: "mA" });
      fail(`jack radio did not apply: state ${st.meter.redJack}; direct apply -> ${JSON.stringify(r)}; radios ${document.querySelectorAll("input[name=dmm-jack]").length}`);
    }
    if (st.supply.currentLimit !== 1) {
      const r = h.apply({ type: "setSupply", currentLimit: 1 });
      fail(`limit did not apply: state ${st.supply.currentLimit}; direct apply -> ${JSON.stringify(r)}`);
    }
    await dragProbe("red", hole("t+2"));
    await dragProbe("black", hole("t-2"));
    await click("tool-wire");
    await tap(h.svg, terminal("psu", "pos"));
    await tap(h.svg, hole("t+3"));
    await tap(h.svg, terminal("psu", "neg"));
    await tap(h.svg, hole("t-3"));
    await click("tool-cancel");
    await h.frames(30);
    check("fuse blown", text("dmm-fuse") === "FUSE BLOWN", `got "${text("dmm-fuse")}", meter "${text("dmm-text")}", leads ${h.svg.querySelectorAll(".lead").length}, red probe "${h.svg.querySelector('.probe-tip[data-probe="red"]')?.getAttribute("aria-label")}", black probe "${h.svg.querySelector('.probe-tip[data-probe="black"]')?.getAttribute("aria-label")}", view ${JSON.stringify((h.view() as { state: unknown; fuseBlown: unknown; supply: unknown }).state)} fuse=${(h.view() as { fuseBlown: unknown }).fuseBlown} supply=${JSON.stringify((h.view() as { supply: unknown }).supply)}`);
    // Replacing the fuse with the probes still across the supply would blow it again.
    await click("lift-red");
    await click("replace-fuse");
    await h.frames(5);
    check("fuse replaced", text("dmm-fuse") === "", `got "${text("dmm-fuse")}"`);
    status.textContent = `SELFTEST OK: ${steps.join(", ")}`;
  } catch (e) {
    status.textContent = `SELFTEST FAILED after [${steps.join(", ")}]: ${(e as Error).message}`;
    status.classList.add("error");
  }
}

/** Drives the guided activity u1-l1-guided through the real controls. Open index.html?activity=u1-l1-guided&selftest=activity */
async function runActivitySelfTest(h: TestHooks): Promise<void> {
  const status = document.getElementById("status")!;
  const steps: string[] = [];
  const fail = (msg: string) => { throw new Error(msg); };
  const check = (name: string, ok: boolean, detail = "") => { if (!ok) fail(`${name} ${detail}`.trim()); steps.push(name); };
  const act = () => h.activity() as { id: string; complete: boolean; passed: boolean; currentStep: number | null; steps: { done: boolean }[]; lastHint: { text: string } | null; goals: { achieved: boolean }[] } | null;
  try {
    await h.frames(2);
    check("activity loaded", act()?.id === "u1-l1-guided", `got ${act()?.id}`);
    check("step 1 current", act()?.currentStep === 0);
    check("tray hidden", (document.getElementById("part-picker") as HTMLElement).hidden && !(document.getElementById("tray-box") as HTMLElement).hidden);
    // Hint before doing anything: the supply is off.
    (document.getElementById("hint-button") as HTMLButtonElement).click();
    await h.frames(3);
    check("hint shown", !!document.getElementById("hint-text")?.textContent?.includes("supply output"), `got "${document.getElementById("hint-text")?.textContent}"`);
    await knob(h, "volt", 5);
    await h.frames(3);
    check("step 1 done", act()?.steps[0].done === true);
    await output(h);
    await h.frames(120);
    check("steps done", act()?.steps.every((s) => s.done) === true, JSON.stringify(act()?.steps));
    check("complete", act()?.complete === true && act()?.passed === true);
    check("banner", !!document.querySelector("#activity .complete"));
    const next = document.getElementById("next-activity") as HTMLButtonElement | null;
    check("next button", !!next && next.textContent!.startsWith("Next: "), `got "${next?.textContent}"`);
    status.textContent = `SELFTEST OK: ${steps.join(", ")}`;
  } catch (e) {
    status.textContent = `SELFTEST FAILED after [${steps.join(", ")}]: ${(e as Error).message}`;
    status.classList.add("error");
  }
}
